// University of California, Santa Cruz: public college page (#2596), official facts only.
//
// Every figure below was read on 2026-09-30 from the source it names:
// #3859 (2026-10-11), page vs KB: the cost table now follows the school's
// own 2026-27 cost of attendance page (cited as its own source), the figures
// the KB holds; the Common Data Set G1 budget it showed before differs.
//  - UC Santa Cruz's Common Data Set 2025-2026 (the "revised" PDF on
//    mediafiles.ucsc.edu): C1 applicants/admits/enrollees for Fall 2025 with
//    the residency table, C8A (no use of SAT or ACT), C21/C22 (no early
//    decision, no early action), G1/G5 2026-2027 costs. The file name says
//    2025-26; its cover and every page header say "Common Data Set 2025-2026".
//  - admissions.ucsc.edu: first-year requirements (standardized test scores),
//    Important Dates (fall 2027 filing period and decision window), and the
//    FAQ on alternate majors (Computer Science). The site sits behind a bot
//    wall, so these pages were read in a browser.
//  - College Scorecard school page, id 110714 (net price by family income;
//    the API was rate-limited, so the figures come from the official page).
// Nothing here is profile-derived, a fit score, anyone's odds, or a
// self-reported record. Re-check a figure against its source before editing
// it, and move `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'UC Santa Cruz Common Data Set 2025-2026',
        url: 'https://mediafiles.ucsc.edu/iraps/common-data-set/common-data-set-2025-26-revised.pdf',
        read: '2026-09-30',
    },
    firstYear: {
        label: 'UC Santa Cruz Admissions, First-Year Students',
        url: 'https://admissions.ucsc.edu/first-year-student',
        read: '2026-09-30',
    },
    dates: {
        label: 'UC Santa Cruz Admissions, Important Dates to Know',
        url: 'https://admissions.ucsc.edu/posts/dates-deadlines',
        read: '2026-09-30',
    },
    faq: {
        label: 'UC Santa Cruz Admissions, Frequently Asked Questions',
        url: 'https://admissions.ucsc.edu/resources-support/frequently-asked-questions-faqs',
        read: '2026-09-30',
    },
    ucGuide: {
        label: 'UC Admissions, Quick Reference Guide to UC Admissions (August 2026)',
        url: 'https://admission.universityofcalifornia.edu/counselors/_files/documents/quick-reference-guide-to-uc-admissions.pdf',
        read: '2026-09-30',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?110714-University-of-California-Santa-Cruz',
        read: '2026-10-15',
    },
    coa: {
        label: 'UC Santa Cruz Financial Aid, Cost to Attend: 2026-27 Undergraduate Budgets for Entering Students',
        url: 'https://financialaid.ucsc.edu/managing-aid/budget-cost-to-attend/',
        read: '2026-10-11',
    },
};

const college = {
    slug: 'university-of-california-santa-cruz',
    kbId: 'university_of_california_santa_cruz',
    name: 'University of California, Santa Cruz',
    shortName: 'UC Santa Cruz',
    location: 'Santa Cruz, CA',
    control: 'Public',
    lastChecked: '2026-10-11',
    sources: SOURCES,
    // From the KB's sourced branding (UCSC brand guide); decoration only.
    branding: {
        primary: '#003C6C',
        secondary: '#FDC700',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_california_santa_cruz_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 66374,
        admitted: 48274,
        enrolled: 4596,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'California residents', applied: 52573, admitted: 37433 },
            { group: 'Out-of-state', applied: 7248, admitted: 6097 },
            { group: 'International', applied: 6554, admitted: 4745 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'UC Santa Cruz\'s Common Data Set reports one first-year acceptance rate for the whole campus, with no rate by school or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early decision and early action',
        note: 'UC Santa Cruz has no early decision or early action plan. The Common Data Set answers no to both.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to UC Santa Cruz with a proposed major. One major has a limit of its own:',
        rows: [
            { school: 'Computer Science', requirement: 'Space is limited. If you are not offered Computer Science, UC Santa Cruz may offer you your alternate major instead, and if you accept it you cannot switch to Computer Science later.' },
        ],
        note: 'For every other major, you have time after you arrive to prepare before you formally declare.',
        source: 'faq',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Application window', value: 'Oct 1 to Nov 30' },
        rows: [
            { plan: 'UC application', deadline: 'November 30', decision: 'Late February to mid-March', binding: false },
        ],
        note: 'One UC application covers every UC campus. It opens August 1, 2026, and you submit it between October 1 and November 30, 2026. Decisions appear on the UC Santa Cruz admissions portal.',
        source: 'dates',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-blind',
        blind: true,
        line: 'UC Santa Cruz does not use SAT or ACT scores in its review and selection, so scores are not considered even if you send them. Scores can still be used to meet the a-g English requirement (area b) and the UC Entry Level Writing Requirement.',
        source: 'firstYear',
        cdsLine: 'The Common Data Set agrees: it answers no to using SAT or ACT scores in admission decisions.',
        cdsWhere: 'section C8A',
    },

    // Changes already in effect for this cycle (#2651 review): they apply to
    // courses taken from fall 2026 on, so to this year's seniors too.
    currentChanges: [
        { topic: 'Math', line: 'A statistics or additional math course taken in fall 2026 or later no longer makes up for a missing or low grade in Algebra II or Math III. It still counts as an extra year of math. Seniors applying this fall: this already applies to courses you take this year.', source: 'ucGuide' },
        { topic: 'Math', line: 'From fall 2026, AP Statistics no longer meets two years of the UC math requirement. AP Precalculus (with a score of 4 or 5), AP Calculus AB, AP Calculus BC and IB HL Mathematics Analysis and Approaches still do.', source: 'ucGuide' },
    ],
    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-09-30',
        changes: [],
        source: 'dates',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition and fees', amounts: [17439, 17439] },
            { item: 'Nonresident tuition', amounts: [0, 39270] },
            { item: 'Housing (on campus)', amounts: [15120, 15120] },
            { item: 'Food', amounts: [6837, 6837] },
            { item: 'Books, course materials, supplies and equipment', amounts: [1212, 1212] },
            { item: 'Transportation', amounts: [993, 993] },
            { item: 'Personal expenses', amounts: [2727, 2727] },
            { item: 'Campus health insurance', amounts: [4209, 4209] },
        ],
        totals: [48537, 87807],
        note: 'UC Santa Cruz\'s budget for entering students living on campus. Tuition and fees are $15,588 in tuition and student services fees plus $1,851 in campus fees.',
        source: 'coa',
        netPrice: {
            average: 18549,
            byIncome: [
                { income: '$0 to $30,000', amount: 9973 },
                { income: '$30,001 to $48,000', amount: 10713 },
                { income: '$48,001 to $75,000', amount: 13446 },
                { income: '$75,001 to $110,000', amount: 18220 },
                { income: 'Over $110,000', amount: 35350 },
            ],
            note: 'What first-year, full-time in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from UC Santa Cruz\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
